import { apiBuilder, makeParameters, prefixApi } from "@zodios/core";
import { z } from "zod";

const activityKinds = [
  "page_view",
  "campaign_change",
  "keyword_change",
  "rule_change",
  "export",
  "saved_view",
  "insight",
  "amc_report",
  "creative",
] as const;

const activityKindSchema = z.enum(activityKinds);

const personSchema = z.object({
  user_id: z.number(),
  name: z.string(),
  email: z.string(),
  role: z.string().nullable(),
  membership_status: z.string().nullable(),
  has_access: z.boolean(),
  is_trellis: z.boolean(),
  invited_at: z.string().nullable(),
  accepted_at: z.string().nullable(),
  last_login: z.string().nullable(),
  date_joined: z.string().nullable(),
});

const activityRowSchema = z.object({
  user_id: z.number(),
  hour: z.string(),
  kind: activityKindSchema,
  detail: z.string(),
  count: z.number(),
});

const userActivityReportSchema = z.object({
  org: z.object({ id: z.number(), name: z.string() }),
  period: z.object({
    start: z.string(),
    end: z.string(),
    previous_start: z.string(),
    previous_end: z.string(),
  }),
  people: personSchema.array(),
  activity: activityRowSchema.array(),
  unavailable_sources: z.string().array(),
  generated_at: z.string(),
});

const personEventSchema = z.object({
  ts: z.string(),
  kind: activityKindSchema,
  detail: z.string(),
  action: z.enum(["created", "updated"]).nullable(),
  label: z.string().nullable(),
  ref_id: z.number().nullable(),
  campaign: z.string().nullable(),
});

const personEventsSchema = z.object({
  events: personEventSchema.array(),
  limit: z.number(),
  unavailable_sources: z.string().array(),
});

/**
 * The report is built by a background job; every request answers at once with the job's state, and
 * `result` only once it is ready, so the client polls the same URL.
 */
const jobStateSchema = <T extends z.ZodTypeAny>(result: T) =>
  z.discriminatedUnion("status", [
    z.object({ status: z.literal("ready"), started_at: z.null(), result }),
    z.object({ status: z.literal("running"), started_at: z.string(), result: z.null() }),
    z.object({ status: z.literal("failed"), started_at: z.string(), result: z.null() }),
  ]);

export type ActivityKind = z.infer<typeof activityKindSchema>;
export type ActivityPerson = z.infer<typeof personSchema>;
export type ActivityRow = z.infer<typeof activityRowSchema>;
export type UserActivityReport = z.infer<typeof userActivityReportSchema>;
export type PersonEvent = z.infer<typeof personEventSchema>;

const periodParameters = makeParameters([
  { name: "start", type: "Query", schema: z.string().optional() },
  { name: "end", type: "Query", schema: z.string().optional() },
  { name: "refresh", type: "Query", schema: z.literal("1").optional() },
]);

export const staffUserActivityApiDefinitions = prefixApi(
  "/api/staff/user-activity",
  apiBuilder({
    alias: "getStaffUserActivity",
    method: "get",
    path: "/:orgId/",
    parameters: periodParameters,
    status: 200,
    response: jobStateSchema(userActivityReportSchema),
  })
    .addEndpoint({
      alias: "getStaffUserActivityPerson",
      method: "get",
      path: "/:orgId/people/:userId/",
      parameters: periodParameters,
      status: 200,
      response: jobStateSchema(personEventsSchema),
    })
    .build(),
);
